/**
 * Checks if an error from RTK Query represents an aborted/cancelled request.
 * Used to filter out intentional aborts (e.g., from component unmounts) from error reporting.
 */
export const isAbortError = (err: unknown): boolean => {
  const maybeError = (err as { error?: unknown })?.error;
  return (
    (typeof maybeError === 'object' &&
      maybeError !== null &&
      'name' in maybeError &&
      ['AbortError', 'CanceledError', 'CancelledError'].includes(
        (maybeError as { name: string }).name
      )) ||
    (typeof err === 'object' &&
      err !== null &&
      'status' in err &&
      (err as any).status === 'CANCELLED')
  );
};

export type ApiErrorResponse = { status: number | string; data?: unknown; error?: unknown };

export const isApiErrorResponse = (error: unknown): error is ApiErrorResponse =>
  typeof error === 'object' && error !== null && 'status' in error;

/**
 * Reads a displayable `detail` string off an API error envelope, but only for the statuses a
 * caller has declared safe to show a user. Gate on status, not body shape: a 5xx carries the
 * same `{detail, debugging_id}` envelope from the shared unhandled-exception handler, and its
 * detail is either "Internal Server Error" or a raw interpolated exception.
 */
export const getSurfaceableApiDetail = (
  error: unknown,
  surfaceableStatuses: readonly number[]
): string | undefined => {
  if (
    !isApiErrorResponse(error) ||
    typeof error.status !== 'number' ||
    !surfaceableStatuses.includes(error.status) ||
    typeof error.data !== 'object' ||
    error.data === null
  ) {
    return undefined;
  }

  const { detail } = error.data as { detail?: unknown };
  return typeof detail === 'string' && detail.trim() !== '' ? detail : undefined;
};

/**
 * Reads `debugging_id` off an API error envelope. The backend logs that same value as
 * `correlation_id`, which makes it the join key from a browser log to the backend log.
 */
export const getApiDebuggingId = (error: unknown): string | undefined => {
  if (!isApiErrorResponse(error) || typeof error.data !== 'object' || error.data === null) {
    return undefined;
  }

  const { debugging_id: debuggingId } = error.data as { debugging_id?: unknown };
  return typeof debuggingId === 'string' ? debuggingId : undefined;
};

/**
 * Reads the status off an API error envelope. `getApiErrorStatus` keeps RTK's transport statuses
 * ('FETCH_ERROR', 'PARSING_ERROR') so a log records what the rejection actually carried;
 * `getApiHttpStatus` drops them for callers that can only act on a real HTTP status.
 */
export const getApiErrorStatus = (error: unknown): number | string | undefined =>
  isApiErrorResponse(error) ? error.status : undefined;

export const getApiHttpStatus = (error: unknown): number | undefined => {
  const status = getApiErrorStatus(error);
  return typeof status === 'number' ? status : undefined;
};

/**
 * Mixed vocabulary by design: an RTK transport status ('FETCH_ERROR', 'PARSING_ERROR') or a JS
 * Error.name ('TypeError'), depending on the rejection shape. A Datadog facet on this is not one enum.
 */
export const getErrorKind = (error: unknown): string | undefined => {
  if (isApiErrorResponse(error) && typeof error.status === 'string') {
    return error.status;
  }
  const name = (error as { name?: unknown })?.name;
  return typeof name === 'string' ? name : undefined;
};
